import Image from "next/image";
import { getVariantImages, type StickId } from "./variant-images";

type ColorOption = { id: string; name: string; swatchHex: string };

// Renders hidden preload <Image>s for whatever colors the caller has already decided are worth warming --
// see use-color-preload-targets.ts for how that list is actually chosen (geometric grid neighbors when the
// picker's open, a flat-array sequence fallback when it's not). This component doesn't know or care which
// strategy produced its list; it just prefetches it.
//
// Deliberately gated on `enabled` (wired to the CURRENT color's own visible image having already finished
// loading, see ProductGallery's onMainImageLoad) rather than firing the moment a color is picked --
// otherwise this background work would compete with the one image the shopper is actually looking at right
// now for the browser's limited per-host connection/bandwidth budget, working against the flicker this
// exists to prevent.
//
// Rendered as real (but visually hidden, not display:none -- Next only fetches an <Image> that's actually
// in the render tree and not display:none) <Image> elements with the IDENTICAL `sizes` string
// ProductGallery's own visible image uses, rather than a plain <link rel="preload">/new Image() on the raw
// file -- the `sizes` attribute's slot-width calculation is resolved from the string itself against the
// viewport, not the element's own rendered box size, so this warms the exact srcset URL the real gallery
// will request even though the box here is 1x1 -- a raw-file preload would warm a different, unoptimized
// URL Next's <Image> never actually requests.
export function ColorImagePreloader({
  colorsToPreload, stick, enabled,
}: {
  colorsToPreload: ColorOption[];
  stick: StickId;
  enabled: boolean;
}) {
  if (!enabled || colorsToPreload.length === 0) return null;

  return (
    <div
      aria-hidden
      style={{ position: "fixed", top: 0, left: 0, width: 1, height: 1, overflow: "hidden", visibility: "hidden", pointerEvents: "none" }}
    >
      {colorsToPreload.map((c) => (
        <div key={c.id} style={{ position: "relative", width: 1, height: 1 }}>
          <Image
            src={getVariantImages({ colorName: c.name, stick })[0]}
            alt=""
            fill
            sizes="(max-width: 900px) 100vw, 55vw"
            // Deprioritized relative to the shopper's actually-visible image -- this is opportunistic
            // background work, not something that should win a bandwidth contention against it.
            fetchPriority="low"
            // Next's default (no `priority`) is `loading="lazy"`, gated on IntersectionObserver actually
            // seeing this element enter the viewport -- the wrong semantic here (this is a deliberate
            // background prefetch, not "the shopper scrolled to it"), and confirmed directly to just not
            // fire at all for a `visibility:hidden` element in this dev environment's own emulated pane.
            // `eager` fetches immediately on render regardless, which is what "preload the next likely
            // click" actually means.
            loading="eager"
          />
        </div>
      ))}
    </div>
  );
}
